import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAllQuestions } from './questionService';

/**
 * Version Service
 * 
 * Manages question snapshots and versioning for the Trilogy system.
 * Ensures backward compatibility by preserving historical question sets.
 */

/**
 * Computes maximum possible scores per pillar
 * @param {Array} questions - Array of questions
 * @returns {Object} Max scores by pillar
 */
function computePillarMaxScores(questions) {
  const maxScores = {
    LEAD: 0,
    EVOLVE: 0,
    ALIGN: 0,
    PERFORM: 0
  };

  questions.forEach(q => {
    if (q.type === 'scored' && q.active) {
      const contribution = (q.maxScore || 5) * (q.weight || 1);
      maxScores[q.pillar] = (maxScores[q.pillar] || 0) + contribution;
    }
  });

  return maxScores;
}

/**
 * Computes total maximum possible score
 * @param {Object} pillarMaxScores - Max scores by pillar
 * @returns {number} Total max score
 */
function computeTotalMaxScore(pillarMaxScores) {
  return Object.values(pillarMaxScores).reduce((sum, score) => sum + score, 0);
}

/**
 * Generates a summary of changes between versions
 * @param {Array} previousQuestions - Questions from previous version
 * @param {Array} currentQuestions - Questions in current version
 * @returns {string} Change summary
 */
function generateChangesSummary(previousQuestions = [], currentQuestions = []) {
  const changes = [];

  // Count added questions
  const previousKeys = new Set(previousQuestions.map(q => q.key));
  const currentKeys = new Set(currentQuestions.map(q => q.key));

  const added = currentQuestions.filter(q => !previousKeys.has(q.key));
  const removed = previousQuestions.filter(q => !currentKeys.has(q.key));
  const modified = currentQuestions.filter(q => {
    const prev = previousQuestions.find(pq => pq.key === q.key);
    return prev && (
      prev.text.en !== q.text.en ||
      prev.text.mr !== q.text.mr ||
      prev.weight !== q.weight ||
      prev.maxScore !== q.maxScore ||
      prev.pillar !== q.pillar
    );
  });

  if (added.length > 0) {
    changes.push(`Added ${added.length} question(s)`);
  }
  if (removed.length > 0) {
    changes.push(`Removed ${removed.length} question(s)`);
  }
  if (modified.length > 0) {
    changes.push(`Modified ${modified.length} question(s)`);
  }

  return changes.length > 0 ? changes.join(', ') : 'Initial version';
}

/**
 * Publishes a new question snapshot
 * @param {string} adminId - Admin user ID
 * @param {string} changesSummary - Optional custom summary
 * @returns {Promise<Object>} Published version
 */
export async function publishSnapshot(adminId, changesSummary = null) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    // Get all active questions
    const questions = await getAllQuestions({ activeOnly: true });

    if (questions.length === 0) {
      throw new Error('Cannot publish snapshot with no active questions');
    }

    // Get latest version number
    const latestVersion = await getLatestVersion();
    const newVersionNumber = latestVersion ? latestVersion.version + 1 : 1;

    // Compute pillar max scores
    const pillarMaxScores = computePillarMaxScores(questions);
    const totalMaxScore = computeTotalMaxScore(pillarMaxScores);

    // Generate changes summary if not provided
    let summary = changesSummary;
    if (!summary && latestVersion) {
      summary = generateChangesSummary(
        latestVersion.questionsSnapshot,
        questions
      );
    } else if (!summary) {
      summary = 'Initial version';
    }

    // Create snapshot document
    const snapshotDoc = {
      version: newVersionNumber,
      questionsSnapshot: questions.map(q => ({
        ...q,
        // Remove Firestore timestamps for clean snapshot
        createdAt: q.createdAt?.toDate?.() || q.createdAt,
        updatedAt: q.updatedAt?.toDate?.() || q.updatedAt
      })),
      pillarMaxScores,
      totalMaxScore,
      publishedBy: adminId,
      publishedAt: serverTimestamp(),
      changesSummary: summary
    };

    // Add to Firestore
    const docRef = await addDoc(collection(db, 'question_versions'), snapshotDoc);

    return {
      id: docRef.id,
      ...snapshotDoc,
      publishedAt: new Date()
    };
  } catch (error) {
    console.error('Error publishing snapshot:', error);
    throw error;
  }
}

/**
 * Gets all published versions
 * @returns {Promise<Array>} Array of versions
 */
export async function getAllVersions() {
  if (!db) {
    console.warn('Firestore is not configured');
    return [];
  }

  try {
    const versionsRef = collection(db, 'question_versions');
    const q = query(versionsRef, orderBy('publishedAt', 'desc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting versions:', error);
    throw error;
  }
}

/**
 * Gets a specific version by ID
 * @param {string} versionId - Version document ID
 * @returns {Promise<Object>} Version data
 */
export async function getVersionById(versionId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const versionRef = doc(db, 'question_versions', versionId);
    const versionSnap = await getDoc(versionRef);

    if (!versionSnap.exists()) {
      throw new Error(`Version not found: ${versionId}`);
    }

    return {
      id: versionSnap.id,
      ...versionSnap.data()
    };
  } catch (error) {
    console.error('Error getting version:', error);
    throw error;
  }
}

/**
 * Gets a version by version number
 * @param {number} versionNumber - Version number
 * @returns {Promise<Object>} Version data
 */
export async function getVersionByNumber(versionNumber) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const versionsRef = collection(db, 'question_versions');
    const q = query(
      versionsRef,
      where('version', '==', versionNumber),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      throw new Error(`Version ${versionNumber} not found`);
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting version by number:', error);
    throw error;
  }
}

/**
 * Gets the latest published version
 * @returns {Promise<Object|null>} Latest version or null
 */
export async function getLatestVersion() {
  if (!db) {
    console.warn('Firestore is not configured');
    return null;
  }

  try {
    const versionsRef = collection(db, 'question_versions');
    const q = query(
      versionsRef,
      orderBy('version', 'desc'),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    };
  } catch (error) {
    console.error('Error getting latest version:', error);
    throw error;
  }
}

/**
 * Compares two versions and returns differences
 * @param {number} version1Number - First version number
 * @param {number} version2Number - Second version number
 * @returns {Promise<Object>} Comparison result
 */
export async function compareVersions(version1Number, version2Number) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const version1 = await getVersionByNumber(version1Number);
    const version2 = await getVersionByNumber(version2Number);

    const questions1 = version1.questionsSnapshot;
    const questions2 = version2.questionsSnapshot;

    const keys1 = new Set(questions1.map(q => q.key));
    const keys2 = new Set(questions2.map(q => q.key));

    // Find added, removed, and modified questions
    const added = questions2.filter(q => !keys1.has(q.key));
    const removed = questions1.filter(q => !keys2.has(q.key));
    const modified = [];
    const unchanged = [];

    questions2.forEach(q2 => {
      const q1 = questions1.find(q => q.key === q2.key);
      if (q1) {
        const changes = {};

        if (q1.text.en !== q2.text.en || q1.text.mr !== q2.text.mr) {
          changes.text = { before: q1.text, after: q2.text };
        }
        if (q1.helpText?.en !== q2.helpText?.en || q1.helpText?.mr !== q2.helpText?.mr) {
          changes.helpText = { before: q1.helpText, after: q2.helpText };
        }
        if (q1.weight !== q2.weight) {
          changes.weight = { before: q1.weight, after: q2.weight };
        }
        if (q1.maxScore !== q2.maxScore) {
          changes.maxScore = { before: q1.maxScore, after: q2.maxScore };
        }
        if (q1.pillar !== q2.pillar) {
          changes.pillar = { before: q1.pillar, after: q2.pillar };
        }
        if (q1.orderIndex !== q2.orderIndex) {
          changes.orderIndex = { before: q1.orderIndex, after: q2.orderIndex };
        }

        if (Object.keys(changes).length > 0) {
          modified.push({
            key: q2.key,
            changes
          });
        } else {
          unchanged.push(q2.key);
        }
      }
    });

    return {
      version1: {
        number: version1.version,
        publishedAt: version1.publishedAt,
        publishedBy: version1.publishedBy
      },
      version2: {
        number: version2.version,
        publishedAt: version2.publishedAt,
        publishedBy: version2.publishedBy
      },
      summary: {
        added: added.length,
        removed: removed.length,
        modified: modified.length,
        unchanged: unchanged.length,
        totalQuestions1: questions1.length,
        totalQuestions2: questions2.length
      },
      details: {
        added: added.map(q => ({
          key: q.key,
          text: q.text,
          pillar: q.pillar,
          type: q.type
        })),
        removed: removed.map(q => ({
          key: q.key,
          text: q.text,
          pillar: q.pillar,
          type: q.type
        })),
        modified
      }
    };
  } catch (error) {
    console.error('Error comparing versions:', error);
    throw error;
  }
}

/**
 * Gets published questions (from latest version)
 * This is the main function used by the user-facing assessment
 * @returns {Promise<Object>} Latest version with questions
 */
export async function getPublishedQuestions() {
  // For development/testing, we will always fetch the live active questions
  // This ensures that changes made in the Admin Panel are immediately visible
  // regardless of whether a snapshot has been published.

  const questions = await getAllQuestions({ activeOnly: true });
  const pillarMaxScores = computePillarMaxScores(questions);

  return {
    version: 0, // 0 indicates draft/live version
    questions,
    pillarMaxScores,
    totalMaxScore: computeTotalMaxScore(pillarMaxScores),
    publishedAt: new Date(),
    isDraft: true
  };

  /* 
  // Original Versioning Logic (Restored later for production)
  const latestVersion = await getLatestVersion();
  
  if (!latestVersion) {
    const questions = await getAllQuestions({ activeOnly: true });
    return {
      version: 0,
      questions,
      pillarMaxScores: computePillarMaxScores(questions),
      totalMaxScore: computeTotalMaxScore(computePillarMaxScores(questions)),
      publishedAt: new Date(),
      isDraft: true
    };
  }

  return {
    version: latestVersion.version,
    questions: latestVersion.questionsSnapshot,
    pillarMaxScores: latestVersion.pillarMaxScores,
    totalMaxScore: latestVersion.totalMaxScore,
    publishedAt: latestVersion.publishedAt,
    isDraft: false
  };
  */
}
